import { useState } from 'react'
import {
  Box,
  Container,
  Text,
  IconButton,
  Spinner,
  Flex,
  Button,
  Card,
  Grid,
} from 'theme-ui'
import { BsFillFileEarmarkTextFill, BsSearch, BsEye } from 'react-icons/bs'
import { useDebounce } from 'use-debounce'
import { useQuery } from 'react-query'

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import { PanelComponent } from '../../components/Panel/Panel'
import { InputComponent } from '../../components/Input/Input'
import { Table } from '../../components'
import api from '../../api/api'
import { date } from '../../utils/formatter'
import { Link } from 'react-router-dom'
import colors from '../../theme/colors'
import useCurrentUserStore from '../../stores/currentUser'
import { match } from 'ts-pattern'
import { ReportAPI } from 'api'
import { groupBy, pipe, map } from 'lodash/fp'
import { humanize } from '../../utils'

export default function HomePage() {
  const [text, setText] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [offset] = useState(0)
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const roles = currentUser.data?.roles
  const { authToken } = currentUser
  const [value] = useDebounce(text, 1000)
  const filters = [value, dateFrom, dateTo]

  const { data: dataSummary } = useQuery<any>(
    ['reports-summary', filters],
    ({ queryKey }) => {
      const [text, dateFrom, dateTo] = queryKey[1] as any[]
      return api.get('/reports', {
        params: {
          'q[no_or_state_cont]': text || undefined,
          'q[created_at_gteq]': dateFrom || undefined,
          'q[created_at_lteq]': dateTo || undefined,
          limit: 999999,
        },
      })
    },
    { enabled: !!authToken }
  )

  const reports: ReportAPI.Data[] = dataSummary?.data?.data || []
  const total = reports.length
  const proven = reports.filter((report) => report.violation)?.length
  const channels = pipe(
    groupBy((report: ReportAPI.Data) => report.channel),
    Object.entries,
    map((channel) => [channel[0], channel[1].length])
  )(reports || [])

  const { data: dataApi, isLoading } = useQuery<any>(
    ['/reports', filters],
    ({ queryKey }) => {
      const [text, dateFrom, dateTo] = queryKey[1] as any[]
      return api.get(`/reports`, {
        params: {
          'q[no_or_state_cont]': text,
          'q[created_at_gteq]': dateFrom || undefined,
          'q[created_at_lteq]': dateTo || undefined,
          'q[state_not_cont]': match(roles)
            .with('admin_reviewer', () => 'verified_to_board')
            .with('admin_board_of_honor', () => 'verified_to_reviewer')
            .otherwise(() => undefined),
          limit: 50,
          offset,
        },
      })
    },
    { enabled: !!authToken }
  )

  const dataReports =
    dataApi?.data?.data.map((data, idx) => {
      return {
        ...data,
        num: idx + 1,
        pengaduan: humanize(data.pengaduan),
        state: humanize(data.state),
        dateComplaint: date(data.dateComplaint),
      }
    }) || []

  const columns = [
    { Header: 'No', accessor: 'num', width: '5%' },
    { Header: 'No. Laporan', accessor: 'no' },
    { Header: 'Pengaduan', accessor: 'pengaduan' },
    { Header: 'Tanggal Pengaduan', accessor: 'dateComplaint' },
    { Header: 'Channel', accessor: 'channel' },
    { Header: 'Status', accessor: 'state' },
    {
      Header: 'Aksi',
      id: 'action',
      Cell: ActionCell,
      width: '5%',
      centered: true,
    },
  ]

  if (!currentUser.authToken) {
    return (
      <Container>
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            textAlign: 'center',
          }}
        >
          <Text as="h1">Mulai buat Laporan</Text>
          <Text sx={{ mb: 4 }}>
            Anda akan mendapatkan Nomor ID setelah membuat laporan
          </Text>
          <Link
            to="laporan-pengaduan/create"
            style={{ textDecoration: 'none' }}
          >
            <Button sx={{ display: 'block', alignSelf: 'center' }}>
              Buat Laporan
            </Button>
          </Link>
        </Flex>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 15 }}>
        <Spinner />
      </Box>
    )
  }

  return (
    <Container sx={{ px: 4, py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumb
          crumbs={[
            { label: 'Home', link: '#' },
            { label: 'Dashboard', link: '#' },
            { label: 'Pengelola', link: '#' },
          ]}
        />
      </Box>

      <Box color="primary" pt={4} pb={4} sx={{ textAlign: 'center' }}>
        <Text sx={{ fontSize: '1.8rem' }}>
          <BsFillFileEarmarkTextFill /> LAPORAN PENGADUAN
        </Text>
      </Box>

      <Grid columns="1fr 1fr 1fr">
        <InputComponent
          label="Cari berdasarkan orderID atau status"
          icon={<BsSearch />}
          onChange={(e) => setText(e.target.value)}
          value={text}
          type="text"
        />

        <InputComponent
          label="Dari Tanggal"
          icon={<BsSearch />}
          onChange={(e) => setDateFrom(e.target.value)}
          value={dateFrom}
          type="date"
        />

        <InputComponent
          label="Hingga Tanggal"
          icon={<BsSearch />}
          onChange={(e) => setDateTo(e.target.value)}
          value={dateTo}
          type="date"
        />
      </Grid>

      <PanelComponent header="Dashboard Pelaporan Pengaduan" mt={3}>
        <Grid columns="1fr 1fr 1fr">
          <Card>
            Laporan Terbukti {proven} / {total}
          </Card>
          {channels.map((c, index) => {
            return (
              <Card key={index}>
                {c[0] || 'Website'} {c[1]}
              </Card>
            )
          })}
        </Grid>
      </PanelComponent>

      <Box>
        <PanelComponent header="Laporan Pengaduan Anda" mt={3}>
          <Table
            options={{
              initialState: {
                pageSize: 10,
              },
              columns: columns,
              data: dataReports,
            }}
            totalData={dataApi?.data?.total}
          />
        </PanelComponent>
      </Box>
    </Container>
  )
}

function ActionCell(props) {
  const { id } = props?.row?.original || {}

  return (
    <Flex sx={{ alignItems: 'center' }}>
      <Link
        to={`/laporan-pengaduan/${id}`}
        style={{ textDecoration: 'none', color: `${colors.text}` }}
      >
        <IconButton>
          <BsEye size={45} />
        </IconButton>
      </Link>
    </Flex>
  )
}
